/**
 * src/lib/horaChile.js
 * La regla única de la hora en el panel (Manuel, 29-08, opción B):
 * TODO se enseña en hora de Chile, esté donde esté el navegador.
 *
 * La otra mitad de la regla vive en el backend (sla_service.a_hora_chile):
 * se guarda y compara en UTC naive, se presenta en America/Santiago. Las
 * marcas ISO del backend viajan sin zona declarada — por convención de la
 * casa SON UTC — así que aquí se les declara la Z antes de convertir. Las
 * que ya traen zona (p. ej. el proximo_run del scheduler) se respetan.
 *
 * ⚠️ Una fecha «solo día» (AAAA-MM-DD) NO pasa por zona: convertirla
 * retrocedería el día (medianoche UTC son las 20-21h del día ANTERIOR en
 * Chile). Un día de calendario no tiene hora que convertir.
 */
const TZ = 'America/Santiago'

const _SOLO_DIA = /^\d{4}-\d{2}-\d{2}$/
const _CON_ZONA = /([zZ]|[+-]\d{2}:?\d{2})$/

/** ISO (naive UTC de la casa, o con zona) → Date del instante correcto. */
export function fechaUTC(iso) {
  if (!iso) return null
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso
  const s = _CON_ZONA.test(iso) ? iso : `${iso}Z`
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/** «HH:MM» en hora de Chile. */
export function horaCL(iso, opts = {}) {
  const d = fechaUTC(iso)
  return d ? d.toLocaleTimeString('es-CL', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ, ...opts,
  }) : null
}

/** Fecha en hora de Chile; una fecha «solo día» se pinta tal cual es. */
export function fechaCL(iso, opts = {}) {
  if (typeof iso === 'string' && _SOLO_DIA.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('es-CL', opts)
  }
  const d = fechaUTC(iso)
  return d ? d.toLocaleDateString('es-CL', { timeZone: TZ, ...opts }) : null
}

/** Fecha y hora en hora de Chile. */
export function fechaHoraCL(iso, opts = {}) {
  const d = fechaUTC(iso)
  return d ? d.toLocaleString('es-CL', { timeZone: TZ, ...opts }) : null
}

/**
 * Fecha y hora en Chile, pero SIN la fecha cuando es la de hoy.
 *
 * Pedido por Manuel (31-08, 2ª ronda) para la cola de trabajos: sus tres
 * columnas de tiempo —encolado, iniciado, terminado— gastaban 203 px cada una
 * (609 px, más de media tabla) en repetir tres veces el mismo día, y con eso
 * ahogaban la columna «Resultado». Casi todo lo que se mira ahí es de hoy.
 *
 * Se abrevia SOLO el día de hoy: en cuanto la marca es de ayer o de antes se
 * pinta entera. Un «14:03» a secas que en realidad fuera del martes pasado
 * sería una hora que miente, y una hora que miente es peor que una larga.
 */
export function fechaHoraCortaCL(iso, opts = {}) {
  const dia = diaISOChileDe(iso)
  if (!dia) return null
  return dia === diaISOChile() ? horaCL(iso, opts) : fechaHoraCL(iso, opts)
}

/** El día de HOY en Chile como «AAAA-MM-DD» (desplazamiento en días: -1 = ayer). */
export function diaISOChile(desplazamiento = 0) {
  const d = new Date(Date.now() + desplazamiento * 86_400_000)
  // en-CA da exactamente AAAA-MM-DD.
  return d.toLocaleDateString('en-CA', { timeZone: TZ })
}

/** El día chileno («AAAA-MM-DD») en que cae un instante dado. */
export function diaISOChileDe(iso) {
  const d = fechaUTC(iso)
  return d ? d.toLocaleDateString('en-CA', { timeZone: TZ }) : null
}
